import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FixedSizeList as List } from 'react-window';
import CreateAssistantModal from '../components/CreateAssistantModal';
import CustomWidget, { WidgetConfig } from '../components/CustomWidget';
import RealTimeChart, { ChartSeries, ChartConfig } from '../components/RealTimeChart';
import ComparativeAnalysis, { Dataset, ComparisonConfig } from '../components/ComparativeAnalysis';
import ExportTools, { ExportConfig } from '../components/ExportTools';
import { useRealTimeData } from '../hooks/useRealTimeData';
// Fallback to internal lightweight layout if react-grid-layout types are missing
// @ts-ignore
// Removed react-grid-layout to avoid build-time dependency resolution issues
// removed external css dependency for build stability
import { 
  FiUsers, FiMessageSquare, FiActivity, FiTrendingUp,
  FiPhone, FiMail, FiDatabase, FiZap, FiCpu, FiBarChart,
  FiLayout, FiEdit, FiPlus, FiSettings
} from 'react-icons/fi';
import { BiBot } from 'react-icons/bi';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import api from '../utils/api';
import ResponsiveChart from '../components/ResponsiveChart';
import { transformDashboardStats, transformSeriesToChart, transformCategoryToChart, guardedFetch } from '../api/adapters/analyticsAdapter';
import type { DashboardStats as AdapterDashboardStats, TrendSeries } from '../api/adapters/analyticsAdapter';

type ExtendedDashboardStats = AdapterDashboardStats & {
  performance?: { throughput: number; latency: number; availability: number };
  systemHealth?: { cpu: number; memory: number; responseTime: number };
};
import type { Assistant } from '../store/slices/assistantSlice';
import toast from 'react-hot-toast';
import { useSocket } from '../contexts/SocketContext';
import { getWsBaseUrl } from '../utils/env';
import { useTheme } from '../contexts/ThemeContext';
import { validateMultipleContrasts, ContrastResult } from '../utils/contrast';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface StatCard {
  title: string;
  value: string | number;
  change: number;
  icon: React.ReactNode;
  color: string;
}

const Dashboard: React.FC = () => {
  const { colors, themeMode } = useTheme();
  const [selectedPeriod, setSelectedPeriod] = useState('7d');
  const [customStart, setCustomStart] = useState<string>('');
  const [customEnd, setCustomEnd] = useState<string>('');
  const [selectedAgentId, setSelectedAgentId] = useState<string>('');
  const [animatedBots, setAnimatedBots] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const [stats, setStats] = useState<ExtendedDashboardStats>({
    activeAssistants: 0,
    totalConversations: 0,
    messagesToday: 0,
    successRate: 0
  });
  const [conversationTrends, setConversationTrends] = useState<TrendSeries>({
    labels: [],
    data: []
  });
  const [channelPerformance, setChannelPerformance] = useState<TrendSeries>({
    labels: [],
    data: []
  });
  const [messageActivity, setMessageActivity] = useState<TrendSeries>({
    labels: [],
    data: []
  });
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [showAssistantModal, setShowAssistantModal] = useState(false);
  
  // New dashboard features state
  const [dashboardMode, setDashboardMode] = useState<'standard' | 'advanced'>('standard');
  const [isEditMode, setIsEditMode] = useState(false);
  const [showComparativeAnalysis, setShowComparativeAnalysis] = useState(false);
  const { socket } = useSocket();

  // Validate contrast ratios when theme changes
  useEffect(() => {
    const colorPairs: Array<[string, string, string]> = [
      ['chartText', colors.tokens.chartAxisLabels, colors.tokens.bgPrimaryDark],
      ['primaryText', colors.tokens.textPrimary, colors.tokens.bgPrimaryDark],
      ['secondaryText', colors.tokens.textSecondary, colors.tokens.bgPrimaryDark],
      ['chartStroke', colors.tokens.chartStroke, colors.tokens.bgPrimaryDark],
    ];
    
    const results = validateMultipleContrasts(colorPairs);
    
    // Log accessibility warnings
    Object.entries(results).forEach(([name, result]) => {
      if (!result.isAccessible) {
        console.warn(`Accessibility warning: ${name} contrast ratio (${result.ratio.toFixed(2)}) does not meet WCAG AA standards (≥4.5:1)`);
      }
    });
  }, [colors, themeMode]);

  // Real-time data hook for WebSocket updates
  const realTimeData = useRealTimeData(
    getWsBaseUrl().replace(/^http/, 'ws') + (getWsBaseUrl().endsWith('/ws') ? '' : '/ws'),
    { autoConnect: dashboardMode === 'advanced', heartbeatInterval: 1000 }
  );

  // Fetch dashboard data
  const fetchDashboardData = async (period: string) => {
    setLoading(true);
    setErrors({});
    setPending({ stats: true, trends: true, channels: true, activity: true, assistants: true });

    const source = new AbortController();
    const timeout = setTimeout(() => source.abort(), 8000);
    try {
      const statsP = guardedFetch(api.get('/analytics/dashboard-stats', { signal: source.signal }));
      const qp = new URLSearchParams({ period });
      if (customStart && customEnd) {
        qp.set('start', new Date(customStart).toISOString());
        qp.set('end', new Date(customEnd).toISOString());
      }
      if (selectedAgentId) {
        qp.set('agentId', selectedAgentId);
      }
      const trendsP = guardedFetch(api.get(`/analytics/conversation-trends?${qp.toString()}`, { signal: source.signal }));
      const channelsP = guardedFetch(api.get('/analytics/channel-performance', { signal: source.signal }));
      const activityP = guardedFetch(api.get('/analytics/message-activity', { signal: source.signal }));
      const assistantsP = guardedFetch(api.get('/assistants', { signal: source.signal }));

      const [statsR, trendsR, channelsR, activityR, assistantsR] = await Promise.all([statsP, trendsP, channelsP, activityP, assistantsP]);

      if (statsR.ok) {
        setStats(transformDashboardStats(statsR.data));
      } else {
        setErrors(prev => ({ ...prev, stats: statsR.error.message }));
      }
      setPending(prev => ({ ...prev, stats: false }));

      if (trendsR.ok) {
        setConversationTrends({
          labels: Array.isArray((trendsR.data as any)?.labels) ? (trendsR.data as any).labels : [],
          data: Array.isArray((trendsR.data as any)?.data) ? (trendsR.data as any).data : [],
        });
      } else {
        setErrors(prev => ({ ...prev, trends: trendsR.error.message }));
      }
      setPending(prev => ({ ...prev, trends: false }));

      if (channelsR.ok) {
        setChannelPerformance({
          labels: Array.isArray((channelsR.data as any)?.labels) ? (channelsR.data as any).labels : [],
          data: Array.isArray((channelsR.data as any)?.data) ? (channelsR.data as any).data : [],
        });
      } else {
        setErrors(prev => ({ ...prev, channels: channelsR.error.message }));
      }
      setPending(prev => ({ ...prev, channels: false }));

      if (activityR.ok) {
        setMessageActivity({
          labels: Array.isArray((activityR.data as any)?.labels) ? (activityR.data as any).labels : [],
          data: Array.isArray((activityR.data as any)?.data) ? (activityR.data as any).data : [],
        });
      } else {
        setErrors(prev => ({ ...prev, activity: activityR.error.message }));
      }
      setPending(prev => ({ ...prev, activity: false }));

      if (assistantsR.ok) {
        const raw = (assistantsR.data as any);
        const list = Array.isArray(raw?.assistants) ? raw.assistants : Array.isArray(raw) ? raw : [];
        setAssistants(list.slice(0, 6));
            } else {
        setErrors(prev => ({ ...prev, assistants: assistantsR.error.message }));
        setAssistants([]);
      }
      setPending(prev => ({ ...prev, assistants: false }));
    } catch (err: any) {
      toast.error('Failed to load dashboard data');
    } finally {
      clearTimeout(timeout);
      setLoading(false);
    }
  };

  const handleExport = async (config: ExportConfig) => {
    const { format, filename } = config;
    toast.loading(`Exporting ${format.toUpperCase()}...`, { id: 'export' });

    try {
      // Minimal extra params: optional date range and agent selector via local component state
      const queryParams = new URLSearchParams({
        type: format,
        dateRange: selectedPeriod,
      });

      if (customStart && customEnd) {
        queryParams.set('start', new Date(customStart).toISOString());
        queryParams.set('end', new Date(customEnd).toISOString());
      }
      if (selectedAgentId) {
        queryParams.set('agentId', selectedAgentId);
      }
      const response = await api.get(`/call-statistics/export?${queryParams.toString()}`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      let downloadFilename = filename || 'export';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch.length > 1) {
          downloadFilename = filenameMatch[1];
        }
      }
      link.setAttribute('download', downloadFilename);
      
      document.body.appendChild(link);
      link.click();
      
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
      window.URL.revokeObjectURL(url);
      
      toast.success('Export successful!', { id: 'export' });
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export failed. Please try again.', { id: 'export' });
    }
  };

  useEffect(() => {
    fetchDashboardData(selectedPeriod);
  }, [selectedPeriod]);

  // Real-time updates via Socket.IO Context with enhanced handling
  useEffect(() => {
    if (!socket) return;

    const onAnalyticsUpdate = (evt: any) => {
      if (!evt?.type || !evt?.payload) return;

      switch (evt.type) {
        case 'conversation-trends':
          setConversationTrends({
            labels: Array.isArray(evt.payload?.labels) ? evt.payload.labels : [],
            data: Array.isArray(evt.payload?.data) ? evt.payload.data : [],
          });
          break;
        case 'channel-performance':
          setChannelPerformance({
            labels: Array.isArray(evt.payload?.labels) ? evt.payload.labels : [],
            data: Array.isArray(evt.payload?.data) ? evt.payload.data : [],
          });
          break;
        case 'message-activity':
          setMessageActivity({
            labels: Array.isArray(evt.payload?.labels) ? evt.payload.labels : [],
            data: Array.isArray(evt.payload?.data) ? evt.payload.data : [],
          });
          break;
        case 'dashboard-stats':
          if (evt.payload) {
            setStats(prevStats => ({
              ...prevStats,
              ...evt.payload,
              // Preserve existing data if new data is incomplete
              activeAssistants: evt.payload.activeAssistants ?? prevStats.activeAssistants,
              totalConversations: evt.payload.totalConversations ?? prevStats.totalConversations,
              messagesToday: evt.payload.messagesToday ?? prevStats.messagesToday,
              successRate: evt.payload.successRate ?? prevStats.successRate
            }));
          }
          break;
      }
    };

    const onMessagesActivity = (payload: any) => {
      if (payload) {
        setMessageActivity({
          labels: Array.isArray(payload?.labels) ? payload.labels : [],
          data: Array.isArray(payload?.data) ? payload.data : [],
        });
      }
    };

    const onPerformanceMetrics = (payload: any) => {
      if (payload && payload.type === 'realtime-update' && payload.payload) {
        // Update real-time performance indicators
        const metrics = payload.payload;
        setStats(prevStats => ({
          ...prevStats,
          // Update performance metrics with real-time data
          performance: metrics.performance || prevStats.performance,
          systemHealth: metrics.systemHealth || prevStats.systemHealth
        }));
      }
    };

    const onAssistantStatus = (payload: any) => {
      if (Array.isArray(payload?.assistants)) {
        setAssistants(payload.assistants.slice(0, 6));
      } else if (payload?.assistant) {
        setAssistants(prev => {
          const idx = prev.findIndex(a => a.id === payload.assistant.id);
          if (idx >= 0) {
            const copy = [...prev];
            copy[idx] = payload.assistant;
            return copy;
          }
          return [payload.assistant, ...prev].slice(0, 6);
        });
      }
    };

    // Subscribe to all relevant events
    socket.on('analytics.update', onAnalyticsUpdate);
    socket.on('messages.activity', onMessagesActivity);
    socket.on('performance.metrics', onPerformanceMetrics);
    socket.on('assistant.status', onAssistantStatus);

    return () => {
      socket.off('analytics.update', onAnalyticsUpdate);
      socket.off('messages.activity', onMessagesActivity);
      socket.off('performance.metrics', onPerformanceMetrics);
      socket.off('assistant.status', onAssistantStatus);
    };
  }, [socket]);

  // Periodic real-time metrics fetch for more frequent updates
  useEffect(() => {
    if (dashboardMode !== 'advanced') return;

    const fetchRealTimeMetrics = async () => {
      try {
        const response = await api.get('/analytics/realtime-metrics');
        if (response.data) {
          // Update performance indicators with real-time data
          setStats(prevStats => ({
            ...prevStats,
            performance: response.data.performance || prevStats.performance,
            systemHealth: response.data.systemHealth || prevStats.systemHealth
          }));
        }
      } catch (error) {
        console.warn('Failed to fetch real-time metrics:', error);
      }
    };

    // Fetch immediately and then every 30 seconds
    fetchRealTimeMetrics();
    const interval = setInterval(fetchRealTimeMetrics, 30000);

    return () => clearInterval(interval);
  }, [dashboardMode]);

  useEffect(() => {
    // Animate random bots
    const interval = setInterval(() => {
      setAnimatedBots(prev => {
        const newBot = Math.floor(Math.random() * Math.min(assistants.length, 6));
        return [...prev.slice(-2), newBot];
      });
    }, 2000);
    return () => clearInterval(interval);
  }, [assistants.length]);

  const statCards: StatCard[] = [
    {
      title: 'Active Assistants',
      value: pending.stats ? '…' : (stats.activeAssistants || 0),
      change: 20,
      icon: <BiBot size={24} />,
      color: 'from-purple-500 to-pink-500'
    },
    {
      title: 'Total Conversations',
      value: pending.stats ? '…' : (stats.totalConversations?.toLocaleString() || '0'),
      change: 15,
      icon: <FiMessageSquare size={24} />,
      color: 'from-blue-500 to-cyan-500'
    },
    {
      title: 'Messages Today',
      value: pending.stats ? '…' : (stats.messagesToday?.toLocaleString() || '0'),
      change: -5,
      icon: <FiActivity size={24} />,
      color: 'from-green-500 to-emerald-500'
    },
    {
      title: 'Success Rate',
      value: pending.stats ? '…' : `${stats.successRate || 0}%`,
      change: 3,
      icon: <FiTrendingUp size={24} />,
      color: 'from-orange-500 to-red-500'
    }
  ];

  const lineChartData = {
    labels: messageActivity.labels || [],
    datasets: [
      {
        label: 'Messages',
        data: messageActivity.data || [],
        fill: true,
        borderColor: colors.tokens.chartStroke,
        backgroundColor: `${colors.tokens.chartStroke}1A`,
        tension: 0.4,
      }
    ]
  };

  const barChartData = transformCategoryToChart(
    { labels: channelPerformance.labels, data: channelPerformance.data },
    'Channel Usage'
  );


  return (
    <div 
      className="min-h-screen"
      data-theme={themeMode}
      style={{
        background: themeMode === 'dark' 
          ? 'linear-gradient(135deg, #111827 0%, #1f2937 50%, #111827 100%)'
          : 'linear-gradient(135deg, #f9fafb 0%, #e5e7eb 50%, #f9fafb 100%)',
        padding: 'var(--space-xl)'
      }}
    >
      {Object.values(errors).filter(Boolean).length > 2 && (
        <div className="mb-4 p-3 rounded-lg" 
             style={{ 
               backgroundColor: 'rgba(239, 68, 68, 0.2)', 
               border: '1px solid rgba(239, 68, 68, 0.5)',
               color: 'var(--color-text-secondary)'
             }}>
          Multiple data sources failed to load. Please retry or check your connection.
        </div>
      )}
      {/* Animated Background Bots */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute"
            style={{
              left: `${15 + i * 15}%`,
              top: `${10 + (i % 3) * 25}%`,
            }}
            animate={animatedBots.includes(i) ? {
              scale: [1, 1.2, 1],
              rotate: [0, 10, -10, 0],
            } : {}}
            transition={{ duration: 0.5 }}
          >
            <div className="relative">
              <motion.div
                animate={{
                  y: [0, -10, 0],
                }}
                transition={{
                  duration: 3 + i,
                  repeat: Infinity,
                  repeatType: 'reverse',
                }}
                className="opacity-10"
              >
                <BiBot size={60 + i * 10} className="text-purple-500" />
              </motion.div>
              {animatedBots.includes(i) && (
                <motion.div
                  initial={{ scale: 0, opacity: 1 }}
                  animate={{ scale: 2, opacity: 0 }}
                  transition={{ duration: 1 }}
                  className="absolute inset-0 flex items-center justify-center"
                >
                  <div className="w-full h-full rounded-full bg-purple-500" />
                </motion.div>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
          Welcome to Your Dashboard
        </h1>
        <p className="text-gray-400 mt-2">Monitor your AI assistants performance in real-time</p>
        
        {/* Period Selector + Agent and Custom Range */}
        <div className="flex flex-wrap items-center gap-2 mt-4">
          {['24h', '7d', '30d', '90d'].map((period) => (
            <motion.button
              key={period}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setSelectedPeriod(period)}
              className={`px-4 py-2 rounded-lg transition-all ${
                selectedPeriod === period
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
              style={selectedPeriod === period ? { color: 'var(--color-text-primary)' } : {}}
            >
              {period}
            </motion.button>
          ))}

          {/* Agent Selector */}
          <select
            value={selectedAgentId}
            onChange={(e) => setSelectedAgentId(e.target.value)}
            className="px-3 py-2 rounded-lg bg-gray-800 text-gray-300 border border-gray-700"
          >
            <option value="">All Agents</option>
            {assistants.map((a: any) => (
              <option key={a.id} value={a.id}>{a.name || a.id}</option>
            ))}
          </select>

          {/* Date Range Picker (custom) */}
          <input
            type="date"
            value={customStart}
            onChange={(e) => setCustomStart(e.target.value)}
            className="px-3 py-2 rounded-lg bg-gray-800 text-gray-300 border border-gray-700"
          />
          <span className="text-gray-400">to</span>
          <input
            type="date"
            value={customEnd}
            onChange={(e) => setCustomEnd(e.target.value)}
            className="px-3 py-2 rounded-lg bg-gray-800 text-gray-300 border border-gray-700"
          />
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => fetchDashboardData(selectedPeriod)}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white"
          >
            Apply
          </motion.button>
        </div>
      </motion.div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2" 
               style={{ borderBottomColor: colors.tokens.chartStroke }}></div>
        </div>
      )}

      {/* Stats Grid */}
      {!loading && (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ scale: 1.05 }}
            className="relative overflow-hidden rounded-2xl p-6 backdrop-blur-lg"
            style={{
              background: 'var(--color-bg-primary)',
              backdropFilter: 'blur(20px)',
              border: '1px solid var(--color-border-primary)',
            }}
          >
            {/* Animated gradient background */}
            <motion.div
              className={`absolute inset-0 opacity-10 bg-gradient-to-br ${stat.color}`}
              animate={{
                scale: [1, 1.2, 1],
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                repeatType: 'reverse',
              }}
            />
            
            <div className="relative z-10" tabIndex={0} aria-label={`${stat.title} ${stat.value}`}>
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-lg bg-gradient-to-br ${stat.color}`}>
                  {stat.icon}
                </div>
                <motion.div
                  animate={{ rotate: stat.change > 0 ? 0 : 180 }}
                  className={`text-sm font-semibold ${
                    stat.change > 0 ? 'text-green-400' : 'text-red-400'
                  }`}
                >
                  {stat.change > 0 ? '↑' : '↓'} {Math.abs(stat.change)}%
                </motion.div>
              </div>
              <h3 className="text-gray-400 text-sm mb-1">{stat.title}</h3>
              <p className="text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{stat.value}</p>
            </div>

            {/* Animated bot icon */}
            <motion.div
              className="absolute -bottom-4 -right-4 opacity-5"
              animate={{
                rotate: 360,
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: 'linear',
              }}
            >
              <BiBot size={80} />
            </motion.div>
          </motion.div>
        ))}
      </div>
      )}

      {/* Charts Section */}
      {!loading && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Line Chart */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="rounded-2xl"
          style={{
            background: 'var(--color-bg-primary)',
            backdropFilter: 'blur(20px)',
            border: '1px solid var(--color-border-primary)',
            padding: 'var(--space-xl)',
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <h3 style={{ 
              fontFamily: 'Space Grotesk, Inter, sans-serif', 
              fontSize: '1.125rem', 
              fontWeight: '600', 
              color: 'var(--color-text-primary)' 
            }}>Message Activity</h3>
            {errors.activity ? (
              <div className="flex items-center gap-2 text-red-300 text-sm">
                <span>{errors.activity}</span>
                <button
                  className="px-2 py-1 rounded bg-red-800/50 hover:bg-red-700/50"
                  onClick={() => fetchDashboardData(selectedPeriod)}
                >Retry</button>
              </div>
            ) : pending.activity ? (
              <div className="text-gray-400 text-sm">Loading…</div>
            ) : null}
          </div>
          <div style={{ height: 300 }}>
            <ResponsiveChart ariaLabel="Message activity over time" data-testid="message-activity-chart">
          <Line 
            data={lineChartData}
            options={{
              responsive: true,
                  maintainAspectRatio: false,
              plugins: {
                legend: {
                  labels: { 
                    color: colors.tokens.textSecondary,
                    font: {
                      family: 'DM Sans, Inter, sans-serif',
                      size: 12,
                      weight: 400
                    }
                  }
                },
                tooltip: {
                  backgroundColor: 'var(--chart-tooltip-bg)',
                  titleColor: colors.tokens.textPrimary,
                  bodyColor: colors.tokens.textPrimary,
                  borderColor: 'var(--chart-tooltip-border)',
                  borderWidth: 1,
                }
              },
              scales: {
                x: {
                  grid: { color: 'var(--chart-grid)' },
                  ticks: { 
                    color: colors.tokens.chartAxisLabels,
                    font: {
                      family: 'DM Sans, Inter, sans-serif',
                      size: 14,
                      weight: 400
                    }
                  }
                },
                y: {
                  grid: { color: 'var(--chart-grid)' },
                  ticks: { 
                    color: colors.tokens.chartAxisLabels,
                    font: {
                      family: 'DM Sans, Inter, sans-serif',
                      size: 14,
                      weight: 400
                    }
                  }
                }
              }
            }}
          />
            </ResponsiveChart>
          </div>
        </motion.div>

        {/* Bar Chart */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 }}
          className="rounded-2xl"
          style={{
            background: 'var(--color-bg-primary)',
            backdropFilter: 'blur(20px)',
            border: '1px solid var(--color-border-primary)',
            padding: 'var(--space-xl)',
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <h3 style={{ 
              fontFamily: 'Space Grotesk, Inter, sans-serif', 
              fontSize: '1.125rem', 
              fontWeight: '600', 
              color: 'var(--color-text-primary)' 
            }}>Channel Distribution</h3>
            {errors.channels ? (
              <div className="flex items-center gap-2 text-red-300 text-sm">
                <span>{errors.channels}</span>
                <button
                  className="px-2 py-1 rounded bg-red-800/50 hover:bg-red-700/50"
                  onClick={() => fetchDashboardData(selectedPeriod)}
                >Retry</button>
              </div>
            ) : pending.channels ? (
              <div className="text-gray-400 text-sm">Loading…</div>
            ) : null}
          </div>
          <div style={{ height: 300 }}>
            <ResponsiveChart ariaLabel="Channel distribution">
          <Bar 
            data={barChartData as any}
            options={{
              responsive: true,
                  maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                tooltip: {
                  backgroundColor: 'var(--chart-tooltip-bg)',
                  titleColor: colors.tokens.textPrimary,
                  bodyColor: colors.tokens.textPrimary,
                  borderColor: 'var(--chart-tooltip-border)',
                  borderWidth: 1,
                }
              },
              scales: {
                x: {
                  grid: { color: 'var(--chart-grid)' },
                  ticks: { 
                    color: colors.tokens.chartAxisLabels,
                    font: {
                      family: 'DM Sans, Inter, sans-serif',
                      size: 14,
                      weight: 400
                    }
                  }
                },
                y: {
                  grid: { color: 'var(--chart-grid)' },
                  ticks: { 
                    color: colors.tokens.chartAxisLabels,
                    font: {
                      family: 'DM Sans, Inter, sans-serif',
                      size: 14,
                      weight: 400
                    }
                  }
                }
              }
            }}
          />
            </ResponsiveChart>
          </div>
        </motion.div>
      </div>
      )}

      {/* Advanced Dashboard Features */}
      {dashboardMode === 'advanced' && (
        <>
          {/* Widget Management Controls */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
            data-testid="advanced-analytics"
          >
            <div className="flex items-center justify-between" style={{ marginBottom: 'var(--space-lg)' }}>
              <div className="flex items-center" style={{ gap: 'var(--space-md)' }}>
                <FiLayout size={20} className="text-blue-500" />
                <h3 style={{ 
                  fontFamily: 'Space Grotesk, Inter, sans-serif', 
                  fontSize: '1.125rem', 
                  fontWeight: '600', 
                  color: 'var(--color-text-primary)' 
                }}>Advanced Analytics</h3>
              </div>
              <div className="flex items-center" style={{ gap: 'var(--space-sm)' }}>
                <button
                  onClick={() => setIsEditMode(!isEditMode)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    isEditMode 
                      ? 'bg-blue-500' 
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                  style={isEditMode ? { color: 'var(--color-text-primary)' } : {}}
                >
                  <FiEdit size={16} />
                  {isEditMode ? 'Exit Edit' : 'Edit Layout'}
                </button>
                <button
                  onClick={() => setShowComparativeAnalysis(!showComparativeAnalysis)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    showComparativeAnalysis
                      ? 'bg-green-500'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                  style={showComparativeAnalysis ? { color: 'var(--color-text-primary)' } : {}}
                >
                  <FiBarChart size={16} />
                  Comparative Analysis
                </button>
                <ExportTools
                  onExport={handleExport}
                  data={{
                    title: `Dashboard Export - ${selectedPeriod}`,
                    data: [stats, conversationTrends, channelPerformance, messageActivity],
                    metadata: {
                      generatedAt: new Date(),
                      source: 'Dashboard'
                    }
                  }}
                  className="bg-gray-800 text-gray-300 hover:bg-gray-700"
                />
              </div>
            </div>

            {/* Real-time Status Indicator */}
            <div className="flex items-center gap-2 text-sm text-gray-400 mb-4">
              <div className={`w-2 h-2 rounded-full ${
                realTimeData.isConnected ? 'bg-green-500' : 'bg-red-500'
              }`} />
              <span>
                Real-time data: {realTimeData.isConnected ? 'Connected' : 'Disconnected'}
                {realTimeData.lastUpdate && (
                  <span className="ml-2">
                    Last update: {realTimeData.lastUpdate.toLocaleTimeString()}
                  </span>
                )}
              </span>
            </div>
          </motion.div>

          {/* Comparative Analysis Section */}
          <AnimatePresence>
            {showComparativeAnalysis && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-6"
              >
                <ComparativeAnalysis
                  datasets={[
                    {
                      id: 'conversations',
                      name: 'Conversations',
                      data: conversationTrends.labels.map((label, i) => ({
                        timestamp: new Date(label),
                        value: conversationTrends.data[i] || 0
                      })),
                      color: colors.tokens.chartStroke
                    },
                    {
                      id: 'messages',
                      name: 'Messages',
                      data: messageActivity.labels.map((label, i) => ({
                        timestamp: new Date(label),
                        value: messageActivity.data[i] || 0
                      })),
                      color: '#EF4444'
                    }
                  ]}
                  config={{
                    type: 'trend',
                    timeRange: {
                      start: new Date(Date.now() - (selectedPeriod === '24h' ? 86400000 : 
                                     selectedPeriod === '7d' ? 604800000 :
                                     selectedPeriod === '30d' ? 2592000000 : 7776000000)),
                      end: new Date()
                    },
                    metrics: ['conversations', 'messages'],
                    groupBy: selectedPeriod === '24h' ? 'hour' : 'day'
                  }}
                  className="rounded-2xl bg-slate-800/80 backdrop-blur-xl border border-gray-700"
                />
              </motion.div>
            )}
          </AnimatePresence>
  
          {/* Real-time Widgets Grid simplified layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8 items-stretch">
            <div className="w-full">
            {/* Real-time Message Activity */}
            <CustomWidget
              widget={{
                id: 'realtime-messages',
                title: 'Real-time Message Activity',
                type: 'chart',
                position: { x: 0, y: 0 },
                size: { width: 500, height: 400 },
                isVisible: true,
                lastUpdated: new Date()
              }}
              isEditing={isEditMode}
              onRefresh={() => fetchDashboardData(selectedPeriod)}
            >
              <RealTimeChart
                series={[
                  {
                    id: 'messages',
                    label: 'Messages per minute',
                    data: messageActivity.labels.map((label, i) => ({
                      timestamp: new Date(label),
                      value: messageActivity.data[i] || 0
                    })),
                    color: '#8B5CF6'
                  }
                ]}
                config={{
                  type: 'line',
                  title: 'Live Message Feed',
                  xAxis: { type: 'time' },
                  yAxis: { label: 'Messages' }
                }}
                isLive={true}
                updateInterval={30000}
                onDataUpdate={async () => {
                  try {
                    const response = await api.get('/analytics/message-activity');
                    if (response.data && response.data.labels && response.data.data) {
                      return [{
                        id: 'messages',
                        label: 'Messages per minute',
                        data: response.data.labels.map((label: string, i: number) => ({
                          timestamp: new Date(label),
                          value: response.data.data[i] || 0
                        })),
                        color: colors.tokens.chartStroke
                      }];
                    }
                    return [];
                  } catch (error) {
                    console.error('Failed to fetch message activity:', error);
                    return [];
                  }
                }}
                height={300}
              />
            </CustomWidget>
            </div>
            <div className="w-full">
              {/* Performance Metrics */}
              <CustomWidget
                widget={{
                  id: 'performance-metrics',
                  title: 'Performance Metrics',
                  type: 'stat',
                  position: { x: 0, y: 0 },
                  size: { width: 500, height: 400 },
                  isVisible: true,
                  lastUpdated: new Date()
                }}
                isEditing={isEditMode}
                onRefresh={() => fetchDashboardData(selectedPeriod)}
              >
                <div className="h-full w-full">
                  <div className="grid grid-cols-2 gap-4 h-full place-content-center">
                  {/* Success Rate with Live Updates */}
                  <motion.div
                    className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-lg p-4 flex flex-col justify-center"
                    whileHover={{ scale: 1.02 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <div className="flex items-center justify-between mb-2">
                    <div style={{ 
                      fontFamily: 'Geist Mono, ui-monospace, monospace', 
                      fontSize: '1.5rem', 
                      fontWeight: '500', 
                        color: 'var(--color-text-primary)'
                      }}>
                        {stats.successRate || 0}%
                      </div>
                      <motion.div
                        animate={{ scale: [1, 1.1, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="w-2 h-2 bg-blue-400 rounded-full"
                      />
                    </div>
                    <div style={{ 
                      fontFamily: 'DM Sans, Inter, sans-serif', 
                      fontSize: '0.875rem', 
                      color: '#93c5fd' 
                    }}>Success Rate</div>
                    <div className="mt-2 h-2 bg-blue-900/30 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-blue-400 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${stats.successRate || 0}%` }}
                        transition={{ duration: 1, ease: "easeInOut" }}
                      />
                    </div>
                  </motion.div>

                  {/* Active Assistants with Status */}
                  <motion.div
                    className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-lg p-4 flex flex-col justify-center"
                    whileHover={{ scale: 1.02 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <div className="flex items-center justify-between mb-2">
                    <div style={{ 
                      fontFamily: 'Geist Mono, ui-monospace, monospace', 
                      fontSize: '1.5rem', 
                      fontWeight: '500', 
                        color: 'var(--color-text-primary)'
                      }}>
                        {stats.activeAssistants || 0}
                      </div>
                      <motion.div
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 3, repeat: Infinity }}
                        className="w-2 h-2 bg-green-400 rounded-full"
                      />
                    </div>
                    <div style={{ 
                      fontFamily: 'DM Sans, Inter, sans-serif', 
                      fontSize: '0.875rem', 
                      color: '#86efac' 
                    }}>Active Assistants</div>
                    <div className="flex items-center mt-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse mr-2" />
                      <span className="text-xs text-green-300">Real-time monitoring</span>
                    </div>
                  </motion.div>

                  {/* Average Messages per Assistant */}
                  <motion.div
                    className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-lg p-4 flex flex-col justify-center"
                    whileHover={{ scale: 1.02 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <div className="flex items-center justify-between mb-2">
                    <div style={{ 
                      fontFamily: 'Geist Mono, ui-monospace, monospace', 
                      fontSize: '1.5rem', 
                      fontWeight: '500', 
                        color: 'var(--color-text-primary)'
                      }}>
                        {stats.activeAssistants ? Math.round((stats.messagesToday || 0) / stats.activeAssistants) : 0}
                      </div>
                      <motion.div
                        animate={{ rotate: [0, 360] }}
                        transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                        className="w-2 h-2 border rounded-full border-t-transparent"
                        style={{ borderColor: 'var(--color-border-primary)', borderTopColor: 'transparent' }}
                      />
                    </div>
                    <div style={{ 
                      fontFamily: 'DM Sans, Inter, sans-serif', 
                      fontSize: '0.875rem', 
                      color: '#d8b4fe' 
                    }}>Avg Msgs/Assistant</div>
                    <div className="mt-2 text-xs text-purple-300">
                      Last 24h: {stats.messagesToday || 0} total
                  </div>
                  </motion.div>

                  {/* Response Time with Live Indicator */}
                  <motion.div
                    className="bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-lg p-4 flex flex-col justify-center"
                    whileHover={{ scale: 1.02 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <div className="flex items-center justify-between mb-2">
                    <div style={{ 
                      fontFamily: 'Geist Mono, ui-monospace, monospace', 
                      fontSize: '1.5rem', 
                      fontWeight: '500', 
                        color: 'var(--color-text-primary)'
                      }}>
                        {Math.round((stats.totalConversations || 0) * 0.85)}
                      </div>
                      <motion.div
                        animate={{
                          scale: [1, 1.1, 1],
                          opacity: [1, 0.7, 1]
                        }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                        className="w-2 h-2 bg-orange-400 rounded-full"
                      />
                    </div>
                    <div style={{ 
                      fontFamily: 'DM Sans, Inter, sans-serif', 
                      fontSize: '0.875rem', 
                      color: '#fdba74' 
                    }}>Avg Response Time (ms)</div>
                    <div className="mt-2 text-xs text-orange-300">
                      <span className={Math.round((stats.totalConversations || 0) * 0.85) < 2000 ? 'text-green-300' : 'text-orange-300'}>
                        {Math.round((stats.totalConversations || 0) * 0.85) < 2000 ? 'Excellent' : 'Good'}
                      </span>
                  </div>
                  </motion.div>
                  </div>
                </div>
              </CustomWidget>
            </div>
          </div>
        </>
      )}

      {/* Dashboard Mode Toggle */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="mb-6"
      >
        <div className="flex items-center justify-center">
          <div className="bg-gray-800 rounded-lg p-1 flex">
            <button
              onClick={() => setDashboardMode('standard')}
              className={`px-4 py-2 rounded-md transition-colors ${
                dashboardMode === 'standard'
                  ? 'bg-blue-500'
                  : 'text-gray-400'
              }`}
              style={dashboardMode === 'standard' ? { color: 'var(--color-text-primary)' } : {}}
            >
              Standard View
            </button>
            <button
              onClick={() => setDashboardMode('advanced')}
              className={`px-4 py-2 rounded-md transition-colors ${
                dashboardMode === 'advanced'
                  ? 'bg-blue-500'
                  : 'text-gray-400'
              }`}
              style={dashboardMode === 'advanced' ? { color: 'var(--color-text-primary)' } : {}}
            >
              Advanced Analytics
            </button>
          </div>
        </div>
      </motion.div>

      {/* Active Assistants Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="rounded-2xl p-6"
        style={{
          background: 'var(--color-bg-primary)',
          backdropFilter: 'blur(20px)',
          border: '1px solid var(--color-border-primary)',
        }}
      >
        <h3 className="text-xl font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>Active Assistants</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {assistants.length > 50 ? (
            <List
              height={420}
              itemCount={Math.min(assistants.length, 150)}
              itemSize={140}
              width="100%"
            >
              {({ index, style }) => {
                const assistant = assistants[index];
                return (
                  <div style={style}>
                    <motion.div
                      key={assistant.id || index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.7 + (index % 6) * 0.1 }}
                      whileHover={{ scale: 1.05, y: -5 }}
                      className="p-4 rounded-xl bg-gradient-to-br from-gray-800 to-gray-900 cursor-pointer"
                      style={{ border: '1px solid var(--color-border-primary)' }}
                      onClick={() => {
                        const params = new URLSearchParams();
                        const a: any = assistant;
                        if (a?.defaultAgentId) params.set('agent', a.defaultAgentId);
                        window.location.href = `/assistants/${assistant.id}/edit${params.toString() ? `?${params.toString()}` : ''}`;
                      }}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <motion.div
                            animate={{ rotate: animatedBots.includes(index) ? [0, 360] : 0 }}
                            transition={{ duration: 1 }}
                            className={`p-2 rounded-lg bg-gradient-to-br ${
                              index % 3 === 0 ? 'from-purple-500 to-pink-500' :
                              index % 3 === 1 ? 'from-blue-500 to-cyan-500' :
                              'from-green-500 to-emerald-500'
                            }`}
                          >
                            <BiBot size={20} />
                          </motion.div>
                          <div>
                            <h4 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{assistant.name || `Assistant ${index + 1}`}</h4>
                            <p className="text-xs text-gray-400">Active now</p>
                          </div>
                        </div>
                        <motion.div
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 2, repeat: Infinity }}
                          className="w-2 h-2 bg-green-500 rounded-full"
                        />
                      </div>
                      <div className="flex justify-between text-xs text-gray-400">
                        <span>{Math.floor(Math.random() * 100) + 20} msgs/hr</span>
                        <span>{Math.floor(Math.random() * 30) + 70}% success</span>
                      </div>
                    </motion.div>
                  </div>
                );
              }}
            </List>
          ) : assistants.length > 0 ? assistants.map((assistant: any, index: number) => (
            <motion.div
              key={assistant.id || index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.7 + index * 0.1 }}
              whileHover={{ scale: 1.05, y: -5 }}
              className="p-4 rounded-xl bg-gradient-to-br from-gray-800 to-gray-900 cursor-pointer"
              style={{ border: '1px solid var(--color-border-primary)' }}
              onClick={() => {
                const params = new URLSearchParams();
                // defaultAgentId is optional UI prop; ignore if absent
                const a: any = assistant;
                if (a?.defaultAgentId) params.set('agent', a.defaultAgentId);
                window.location.href = `/assistants/${assistant.id}/edit${params.toString() ? `?${params.toString()}` : ''}`;
              }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <motion.div
                    animate={{
                      rotate: animatedBots.includes(index) ? [0, 360] : 0,
                    }}
                    transition={{ duration: 1 }}
                    className={`p-2 rounded-lg bg-gradient-to-br ${
                      index % 3 === 0 ? 'from-purple-500 to-pink-500' :
                      index % 3 === 1 ? 'from-blue-500 to-cyan-500' :
                      'from-green-500 to-emerald-500'
                    }`}
                  >
                    <BiBot size={20} />
                  </motion.div>
                  <div>
                    <h4 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{assistant.name || `Assistant ${index + 1}`}</h4>
                    <p className="text-xs text-gray-400">Active now</p>
                  </div>
                </div>
                <motion.div
                  animate={{
                    scale: [1, 1.2, 1],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                  }}
                  className="w-2 h-2 bg-green-500 rounded-full"
                />
              </div>
              <div className="flex justify-between text-xs text-gray-400">
                <span>{Math.floor(Math.random() * 100) + 20} msgs/hr</span>
                <span>{Math.floor(Math.random() * 30) + 70}% success</span>
              </div>
            </motion.div>
          )) : (
            <div className="col-span-full text-center text-gray-400 py-8">
              No assistants created yet. Create your first assistant to get started!
            </div>
          )}
        </div>
        {errors.assistants && (
          <div className="mt-4 flex items-center gap-2 text-red-300 text-sm">
            <span>{errors.assistants}</span>
            <button
              className="px-2 py-1 rounded bg-red-800/50 hover:bg-red-700/50"
              onClick={() => fetchDashboardData(selectedPeriod)}
            >Retry</button>
          </div>
        )}
      </motion.div>

      {/* Quick Actions */}
      {!loading && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="mt-8 flex flex-wrap gap-4"
      >
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="px-6 py-3 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 font-semibold"
          style={{ color: 'var(--color-text-primary)' }}
          onClick={() => setShowAssistantModal(true)}
        >
          Create New Assistant
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="px-6 py-3 rounded-lg bg-gray-800 text-gray-300 font-semibold hover:bg-gray-700"
          onClick={() => (window.location.href = '/conversations')}
        >
          View All Conversations
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="px-6 py-3 rounded-lg bg-gray-800 text-gray-300 font-semibold hover:bg-gray-700"
          onClick={() => (window.location.href = '/analytics-report')}
        >
          Analytics Report
        </motion.button>
      </motion.div>
      )}
      {showAssistantModal && (
        <CreateAssistantModal
          isOpen={showAssistantModal}
          onClose={() => setShowAssistantModal(false)}
          onSuccess={() => {
            setShowAssistantModal(false);
            fetchDashboardData(selectedPeriod);
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;